import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, MessageSquare, Reply, Send, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");
const CH = { email: ["Email", Mail], sms: ["SMS", Smartphone], whatsapp: ["WhatsApp", MessageSquare] };
const GSM7 = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€";
export const smsStats = (t) => {
  const uni = [...t].some((c) => !GSM7.includes(c) && !GSM7_EXT.includes(c));
  const len = uni ? t.length : [...t].reduce((n, c) => n + (GSM7_EXT.includes(c) ? 2 : 1), 0);
  const limit = uni ? 70 : 160;
  return { len, limit, uni, ok: len <= limit };
};

// Scheda cliente: invio diretto email / SMS / WhatsApp (template Meta) nel rispetto dei consensi
export default function CustomerSendPanel({ customer }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = user?.role === "admin" || (user?.permissions || []).includes("send_customer_message");
  const [ch, setCh] = useState(null);
  const [comms, setComms] = useState([]);
  const [channel, setChannel] = useState(null);
  const [kind, setKind] = useState("service");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tpl, setTpl] = useState("");
  const [params, setParams] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadComms = () => api.get(`/customers/${customer.id}/comms`).then(({ data }) => setComms(data)).catch(() => {});
  useEffect(() => {
    if (!allowed) return;
    setChannel(null); setBody(""); setSubject(""); setTpl("");
    api.get(`/customers/${customer.id}/channels`).then(({ data }) => setCh(data)).catch(() => setCh(null));
    loadComms();
  }, [customer.id, allowed]); // eslint-disable-line
  const template = useMemo(() => (ch?.whatsapp?.templates || []).find((t) => t.name === tpl), [ch, tpl]);
  useEffect(() => { setParams(Array.from({ length: template?.n_params || 0 }, (_, i) => params[i] || "first_name")); }, [template]); // eslint-disable-line
  if (!allowed || !ch) return null;
  const avail = Object.keys(CH).filter((k) => ch[k].configured && ch[k].has_contact);
  if (!avail.length) return null;
  const blocked = channel && kind === "marketing" && !ch[channel].marketing_consent;
  const treatBlocked = !ch.treatment_ok;
  const sms = smsStats(body);
  const preview = template ? params.reduce((s, p, i) => s.replace(new RegExp(`{{\\s*${i + 1}\\s*}}`, "g"), `[${ch.wa_vars[p] || p}]`), template.body) : "";
  const canSend = channel && !blocked && !treatBlocked && (channel === "email" ? subject.trim() && body.trim() : channel === "sms" ? body.trim() && sms.ok : !!template);
  const send = async () => {
    setBusy(true);
    try {
      const payload = { channel, kind, subject: subject || null, body: body || null, wa_template: channel === "whatsapp" ? { name: template.name, language: template.language || "it", params } : null };
      await api.post(`/customers/${customer.id}/send`, payload);
      toast.success(`${CH[channel][0]} inviato a ${customer.name}`); setBody(""); setSubject(""); setChannel(null); loadComms();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl bg-zinc-950 border border-zinc-800 space-y-3" data-testid="customer-send-panel">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Invia comunicazione</div>
      <div className="flex flex-wrap gap-2">
        {avail.map((k) => { const [label, Icon] = CH[k]; return (
          <button key={k} type="button" onClick={() => setChannel(channel === k ? null : k)} data-testid={`send-channel-${k}`}
            className={`pill inline-flex items-center gap-1.5 text-xs ${channel === k ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 text-zinc-300 border border-zinc-800"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>); })}
      </div>
      {channel && (
        <div className="space-y-2" data-testid="send-form">
          <div className="flex gap-3 text-xs text-zinc-300">
            {[["service", "Comunicazione di servizio"], ["marketing", "Promozionale (marketing)"]].map(([v, l]) => (
              <label key={v} className="inline-flex items-center gap-1.5"><input type="radio" name="kind" value={v} checked={kind === v} onChange={() => setKind(v)} data-testid={`send-kind-${v}`} /> {l}</label>
            ))}
          </div>
          {treatBlocked && <div className="text-xs text-red-300" data-testid="send-blocked-treatment">Il cliente ha revocato il consenso al trattamento dei dati: nessun invio possibile.</div>}
          {blocked && <div className="text-xs text-red-300" data-testid="send-blocked-consent">Consenso marketing {channel === "email" ? "email" : "WhatsApp/SMS"} non prestato: puoi inviare solo comunicazioni di servizio.</div>}
          {channel === "email" && <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto" className="bg-zinc-900 border-zinc-800" data-testid="send-subject" />}
          {channel !== "whatsapp" && (
            <div>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={channel === "sms" ? 3 : 5} className="bg-zinc-900 border-zinc-800" data-testid="send-body"
                placeholder={channel === "sms" ? "Testo SMS (max 160 caratteri)" : "Testo del messaggio… puoi usare {{name}}, {{points}}, {{card}}"} />
              {channel === "sms" && <div className={`text-[11px] mt-1 ${sms.ok ? "text-zinc-500" : "text-red-300"}`} data-testid="sms-counter">{sms.len}/{sms.limit} caratteri{sms.uni ? " · caratteri speciali/emoji: limite 70" : ""}{!sms.ok ? " · supera il limite" : ""}</div>}
            </div>
          )}
          {channel === "whatsapp" && (
            <div className="space-y-2">
              <select value={tpl} onChange={(e) => setTpl(e.target.value)} className="w-full bg-zinc-900 border border-zinc-800 rounded-md h-9 px-2 text-sm" data-testid="send-wa-template">
                <option value="">Scegli un template approvato…</option>
                {ch.whatsapp.templates.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.language}{t.category ? ` · ${t.category}` : ""})</option>)}
              </select>
              {template && params.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Label className="text-zinc-400 w-24">Variabile {`{{${i + 1}}}`}</Label>
                  <select value={p} onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md h-8 px-2 text-xs" data-testid={`send-wa-param-${i}`}>
                    {Object.entries(ch.wa_vars).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
              ))}
              {template && <div className="text-xs text-zinc-300 rounded-lg border border-zinc-800 p-2 whitespace-pre-line" data-testid="send-wa-preview">{preview}</div>}
            </div>
          )}
          <Button onClick={send} disabled={!canSend || busy} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 h-9" data-testid="send-submit"><Send className="h-4 w-4 mr-1" /> Invia {CH[channel][0]}</Button>
        </div>
      )}
      {comms.length > 0 && (
        <ul className="space-y-1 max-h-48 overflow-y-auto" data-testid="send-history">
          {comms.map((m) => m.direction === "in" ? (
            <li key={m.id} className="text-[11px] rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid="comms-inbound">
              <div className="flex items-center justify-between gap-2">
                <div className="text-emerald-300 inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {fmt(m.created_at)} · Risposta WhatsApp del cliente</div>
                <button type="button" onClick={() => navigate(`/messaggi?customer=${customer.id}`)} className="pill bg-emerald-500 text-zinc-950 text-[10px] font-bold inline-flex items-center gap-1" data-testid="comms-reply-button"><Reply className="h-3 w-3" /> Rispondi</button>
              </div>
              <div className="text-zinc-100 whitespace-pre-line mt-1">{m.body}</div>
            </li>
          ) : (
            <li key={m.id} className="text-[11px] rounded-lg border border-zinc-800 p-2">
              <div className="text-zinc-400">{fmt(m.created_at)} · {CH[m.channel]?.[0] || m.channel} · {m.kind === "marketing" ? "promozionale" : "servizio"}{m.operator_name ? ` · ${m.operator_name}` : ""}</div>
              <div className="text-zinc-200 truncate">{m.subject ? `${m.subject} — ` : ""}{m.template ? `[${m.template}] ` : ""}{m.body}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
